import { Suspense, useEffect, useMemo, useRef } from "react";
import { Canvas, useFrame, useLoader, useThree } from "@react-three/fiber";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import * as THREE from "three";

const MODEL = "/models/bmw.glb";
const CAR_LEN = 4.7;
const HOOD_OPEN = 0.95; // rad
const V = (x = 0, y = 0, z = 0) => new THREE.Vector3(x, y, z);

function Environment() {
  const { gl, scene } = useThree();
  useEffect(() => {
    const pmrem = new THREE.PMREMGenerator(gl);
    const env = pmrem.fromScene(new RoomEnvironment(), 0.03);
    scene.environment = env.texture;
    scene.environmentIntensity = 1.1;
    return () => {
      env.dispose();
      pmrem.dispose();
    };
  }, [gl, scene]);
  return null;
}

function shadowTexture() {
  const c = document.createElement("canvas");
  c.width = c.height = 256;
  const ctx = c.getContext("2d");
  const g = ctx.createRadialGradient(128, 128, 10, 128, 128, 126);
  g.addColorStop(0, "rgba(0,0,0,0.85)");
  g.addColorStop(0.55, "rgba(0,0,0,0.35)");
  g.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 256, 256);
  return new THREE.CanvasTexture(c);
}

function Car({ anchorsRef, shotRef }) {
  const gltf = useLoader(GLTFLoader, MODEL);
  const zoneMatsRef = useRef(null);
  const pivot = useRef(null);
  const axis = useRef(V(1, 0, 0));
  const hood = useRef(0);

  const root = useMemo(() => {
    const car = gltf.scene.clone(true);
    const g = new THREE.Group();
    g.add(car);
    const b0 = new THREE.Box3().setFromObject(car);
    const s0 = b0.getSize(V());
    car.scale.setScalar(CAR_LEN / Math.max(s0.x, s0.z));
    car.updateMatrixWorld(true);
    const b1 = new THREE.Box3().setFromObject(car);
    const c1 = b1.getCenter(V());
    car.position.set(-c1.x, -b1.min.y, -c1.z);
    car.updateMatrixWorld(true);
    return g;
  }, [gltf]);

  useEffect(() => {
    root.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(root);
    const size = box.getSize(V());
    const center = box.getCenter(V());

    // hood subtree (top-most nodes only)
    const hoodNodes = [];
    root.traverse((o) => {
      if (/Hood/i.test(o.name)) hoodNodes.push(o);
    });
    const tops = hoodNodes.filter((n) => !hoodNodes.some((p) => p !== n && n.parent && isAncestor(p, n)));
    const hoodBox = new THREE.Box3();
    tops.forEach((n) => hoodBox.expandByObject(n));
    const hoodCenter = hoodBox.isEmpty() ? center.clone() : hoodBox.getCenter(V());

    const zIsLength = size.z >= size.x;
    const lenAxis = zIsLength ? V(0, 0, 1) : V(1, 0, 0);
    const len = zIsLength ? size.z : size.x;
    const halfW = (zIsLength ? size.x : size.z) / 2;
    const sign = Math.sign((zIsLength ? hoodCenter.z : hoodCenter.x) - (zIsLength ? center.z : center.x)) || 1;
    const forward = lenAxis.clone().multiplyScalar(sign);
    const up = V(0, 1, 0);
    const side = up.clone().cross(forward).normalize();
    axis.current = side.clone();

    if (tops.length) {
      const hoodSize = hoodBox.getSize(V());
      const hoodLen = zIsLength ? hoodSize.z : hoodSize.x;
      const hinge = hoodCenter.clone().addScaledVector(forward, -hoodLen / 2);
      hinge.y = hoodBox.max.y;
      const p = new THREE.Group();
      p.position.copy(hinge);
      root.add(p);
      tops.forEach((n) => p.attach(n));
      pivot.current = p;
    }

    const steerNode = findByName(root, /SteeringWheel/i);
    const steerBox = steerNode ? new THREE.Box3().setFromObject(steerNode) : null;
    const steer = steerBox
      ? steerBox.getCenter(V())
      : center.clone().addScaledVector(up, size.y * 0.3);
    const steerSize = steerBox ? steerBox.getSize(V()) : V(0.36, 0.36, 0.36);
    const wheelR = Math.max(steerSize.x, steerSize.y, steerSize.z) / 2 || 0.18;
    const lateral = side.dot(steer.clone().sub(center));
    const inward = side.clone().multiplyScalar(-(Math.sign(lateral) || 1));
    // exact lateral direction from the driver's seat toward the car centreline
    const along = forward.dot(steer.clone().sub(center));
    const onAxis = center.clone().addScaledVector(forward, along);
    const toAxis = onAxis.clone().sub(steer);
    toAxis.y = 0;
    if (toAxis.length() > 0.02) inward.copy(toAxis.normalize());
    const lateralDist = Math.abs(lateral) || 0.38;
    // measured off bmw.glb: driver's emissive instrument cluster relative to the wheel hub
    const speedo = steer
      .clone()
      .addScaledVector(forward, 0.25)
      .addScaledVector(up, -0.015);
    const cluster = speedo.clone();
    // gear selector: centre console top, 0.20 behind the hub, on the centreline, 0.195 lower
    const gearLever = steer
      .clone()
      .addScaledVector(inward, lateralDist)
      .addScaledVector(forward, -0.2)
      .addScaledVector(up, -0.195);

    const wheelY = box.min.y + size.y * 0.2;
    // body stays black; x-ray only the panels that hide the highlighted part
    const groups = new Map();
    const cache = new Map();
    root.traverse((o) => {
      if (!o.material) return;
      const mats = Array.isArray(o.material) ? o.material : [o.material];
      mats.forEach((mat) => {
        if (mat.name && mat.name.includes("CarPaint")) {
          mat.color.set("#07080c");
          mat.metalness = 0.92;
          mat.roughness = 0.2;
          if ("clearcoat" in mat) {
            mat.clearcoat = 1;
            mat.clearcoatRoughness = 0.04;
          }
        }
      });
      if (/Interior|SteeringWheel/i.test(pathName(o))) return;
      const p = pathName(o);
      const zones = [];
      if (/Hood/i.test(p)) zones.push("hood");
      else if (/DoorL|DoorR|Glass|Mirror/i.test(p)) zones.push("cabin");
      else if (/Wheel|Rim/i.test(p)) zones.push("wheel");
      if (!zones.length) return;
      const gkey = zones.join("|");
      const next = mats.map((mat) => {
        const key = `${gkey}:${mat.uuid}`;
        let c = cache.get(key);
        if (!c) {
          c = mat.clone();
          c.name = mat.name;
          cache.set(key, c);
          const g = groups.get(gkey) || { zones, mats: [], fade: 1 };
          g.mats.push(c);
          groups.set(gkey, g);
        }
        return c;
      });
      o.material = Array.isArray(o.material) ? next : next[0];
    });
    zoneMatsRef.current = [...groups.values()];
    if (typeof window !== "undefined") {
      window.__carRoot = root;
      window.__zones = [...groups.entries()].map(([k, g]) => [k, g.mats.length]);
    }

    anchorsRef.current = {
      center,
      forward,
      side,
      up,
      len,
      box,
      size,
      steer,
      inward,
      cluster,
      speedo,
      gearLever,
      engineBay: hoodBox.isEmpty()
        ? center.clone().addScaledVector(forward, len * 0.33).setY(box.min.y + size.y * 0.55)
        : hoodCenter.clone().setY(hoodBox.max.y - size.y * 0.06),
      frontWheel: center
        .clone()
        .addScaledVector(forward, len * 0.32)
        .addScaledVector(side, halfW * 0.9)
        .setY(wheelY),
      rearWheel: center
        .clone()
        .addScaledVector(forward, -len * 0.31)
        .addScaledVector(side, halfW * 0.9)
        .setY(wheelY),
      gearbox: center.clone().addScaledVector(forward, len * 0.02).setY(box.min.y + size.y * 0.16),
    };
    if (typeof window !== "undefined") window.__anchors = anchorsRef.current;
  }, [root, anchorsRef]);

  useFrame((_, dt) => {
    const shot = shotRef.current || {};
    const wantHood = shot.hood ? HOOD_OPEN : 0;
    hood.current += (wantHood - hood.current) * Math.min(1, dt * 2.2);
    if (pivot.current) pivot.current.setRotationFromAxisAngle(axis.current, hood.current);

    const wantFade = typeof shot.xray === "number" ? shot.xray : 0.2;
    const groups = zoneMatsRef.current;
    if (groups) {
      groups.forEach((g) => {
        const want = g.zones.includes(shot.zone) ? wantFade : 1;
        g.fade += (want - g.fade) * Math.min(1, dt * 2.4);
        g.mats.forEach((mat) => {
          mat.transparent = g.fade < 0.995;
          mat.opacity = g.fade;
          mat.depthWrite = g.fade > 0.9;
        });
      });
    }
  });

  return <primitive object={root} />;
}

function isAncestor(maybeAncestor, node) {
  let p = node.parent;
  while (p) {
    if (p === maybeAncestor) return true;
    p = p.parent;
  }
  return false;
}

function pathName(o) {
  let p = o;
  let s = "";
  while (p) {
    s += `|${p.name}`;
    p = p.parent;
  }
  return s;
}

function findByName(root, re) {
  let hit = null;
  root.traverse((o) => {
    if (!hit && re.test(o.name)) hit = o;
  });
  return hit;
}

function resolveShot(a, shot, time) {
  const { center, forward, side, up, len, engineBay, rearWheel, gearbox, speedo, inward, gearLever } = a;
  const shots = {
    engine: {
      focus: engineBay,
      point: engineBay,
      cam: engineBay
        .clone()
        .addScaledVector(forward, len * 0.5)
        .addScaledVector(side, len * 0.4)
        .addScaledVector(up, len * 0.36),
    },
    engineTight: {
      focus: engineBay,
      point: engineBay,
      cam: engineBay
        .clone()
        .addScaledVector(forward, len * 0.3)
        .addScaledVector(side, len * 0.26)
        .addScaledVector(up, len * 0.24),
    },
    cabin: {
      focus: speedo,
      point: speedo,
      cam: speedo
        .clone()
        .addScaledVector(inward, -1.03)
        .addScaledVector(forward, -0.98)
        .addScaledVector(up, 0.47),
    },
    console: {
      focus: gearLever,
      point: gearLever,
      cam: gearLever
        .clone()
        .addScaledVector(inward, -1.42)
        .addScaledVector(forward, -0.27)
        .addScaledVector(up, 0.67),
    },
    wheel: {
      focus: rearWheel,
      point: rearWheel,
      cam: rearWheel
        .clone()
        .addScaledVector(side, len * 0.5)
        .addScaledVector(forward, -len * 0.16)
        .addScaledVector(up, len * 0.14),
    },
    gearbox: {
      focus: gearbox,
      point: gearbox,
      cam: gearbox
        .clone()
        .addScaledVector(side, len * 0.95)
        .addScaledVector(forward, -len * 0.12)
        .addScaledVector(up, len * 0.22),
    },
    sideFull: {
      focus: center.clone().addScaledVector(up, a.size.y * 0.04),
      point: center.clone().addScaledVector(up, a.size.y * 0.3),
      cam: center
        .clone()
        .addScaledVector(side, len * 1.18)
        .addScaledVector(forward, len * 0.06)
        .addScaledVector(up, len * 0.16),
    },
  };
  const ang = time * 0.13;
  const idleFocus = center.clone().addScaledVector(up, -a.size.y * 0.06);
  const idle = {
    focus: idleFocus,
    point: center.clone().addScaledVector(up, a.size.y * 0.42),
    cam: center
      .clone()
      .addScaledVector(side, Math.cos(ang) * len * 1.35)
      .addScaledVector(forward, Math.sin(ang) * len * 1.25)
      .addScaledVector(up, len * 0.4),
  };
  return shots[shot?.view] || idle;
}

function Rig({ anchorsRef, shotRef, clockRef }) {
  const { camera, size } = useThree();
  const camTarget = useRef(V(6, 2.6, 7));
  const lookTarget = useRef(V(0, 0.7, 0));
  const look = useRef(V(0, 0.7, 0));

  useFrame((_, dt) => {
    const a = anchorsRef.current;
    clockRef.current += dt;
    if (a) {
      const { focus, cam } = resolveShot(a, shotRef.current, clockRef.current);
      // narrow (mobile) viewports crop horizontally — pull the camera back so the car fits
      const aspect = size.width / Math.max(size.height, 1);
      const pull = aspect < 1.5 ? Math.min(1.9, 1.5 / Math.max(aspect, 0.55)) : 1;
      const framed = focus.clone().addScaledVector(cam.clone().sub(focus), pull);
      if ([framed.x, framed.y, framed.z, focus.x, focus.y, focus.z].every(Number.isFinite)) {
        camTarget.current.copy(framed);
        lookTarget.current.copy(focus);
      }
    }
    const k = 1 - Math.pow(0.0025, dt);
    camera.position.lerp(camTarget.current, k);
    look.current.lerp(lookTarget.current, k);
    camera.lookAt(look.current);
  });

  return null;
}

// keeps the HTML annotation glued to the real 3D part it describes
function Pointer({ anchorsRef, shotRef, pointerRef, clockRef }) {
  const { camera, size } = useThree();
  const cur = useRef(null);
  const tmp = useRef(V());

  useFrame(() => {
    const a = anchorsRef.current;
    const el = pointerRef?.current;
    if (!a || !el) return;
    const { point } = resolveShot(a, shotRef.current, clockRef.current);
    if (!Number.isFinite(point.x)) return;
    if (!cur.current) cur.current = point.clone();
    else cur.current.lerp(point, 0.16);
    tmp.current.copy(cur.current).project(camera);
    const x = Math.min(Math.max((tmp.current.x * 0.5 + 0.5) * size.width, 24), size.width - 24);
    const y = Math.min(
      Math.max((-tmp.current.y * 0.5 + 0.5) * size.height, size.width < 640 ? 88 : 128),
      size.height - 24
    );
    el.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    const label = el.querySelector("[data-label]");
    if (label) {
      const w = label.offsetWidth;
      const clamped = Math.min(Math.max(x, w / 2 + 12), size.width - w / 2 - 12);
      label.style.transform = `translateX(calc(-50% + ${(clamped - x).toFixed(1)}px))`;
    }
  });

  return null;
}

function CabinLight({ anchorsRef, shotRef, clockRef }) {
  const ref = useRef(null);
  useFrame(() => {
    const a = anchorsRef.current;
    if (!a || !ref.current) return;
    const inside = ["cabin", "console"].includes(shotRef.current?.view);
    const { point } = resolveShot(a, shotRef.current, clockRef.current);
    const p = point.clone().addScaledVector(a.up, 0.22).addScaledVector(a.inward, -0.15);
    ref.current.position.lerp(inside ? p : a.steer.clone().addScaledVector(a.up, 0.3), 0.1);
    ref.current.intensity += ((inside ? 4.5 : 0.6) - ref.current.intensity) * 0.08;
    ref.current.distance = inside ? a.len * 0.5 : a.len * 0.9;
  });
  return <pointLight ref={ref} intensity={0.6} color="#fff3e6" />;
}

function Floor() {
  const tex = useMemo(shadowTexture, []);
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.006, 0]}>
      <planeGeometry args={[9, 6]} />
      <meshBasicMaterial map={tex} transparent depthWrite={false} />
    </mesh>
  );
}

function DebugAnchors({ anchorsRef }) {
  const g = useRef(null);
  useFrame(() => {
    const a = anchorsRef.current;
    if (!a || !g.current || g.current.userData.done) return;
    g.current.children[0].position.copy(a.speedo);
    g.current.children[1].position.copy(a.gearLever);
    g.current.children[2].position.copy(a.steer);
  });
  return (
    <group ref={g}>
      <mesh>
        <sphereGeometry args={[0.05, 12, 12]} />
        <meshBasicMaterial color="#ff0033" depthTest={false} />
      </mesh>
      <mesh>
        <sphereGeometry args={[0.05, 12, 12]} />
        <meshBasicMaterial color="#00ff66" depthTest={false} />
      </mesh>
      <mesh>
        <sphereGeometry args={[0.04, 12, 12]} />
        <meshBasicMaterial color="#2288ff" depthTest={false} />
      </mesh>
    </group>
  );
}

export const Car3D = ({ shotRef, pointerRef }) => {
  const anchorsRef = useRef(null);
  const clockRef = useRef(0);
  const dbg = typeof window !== "undefined" && window.location.search.includes("dbg");
  return (
    <Canvas
      dpr={[1, 1.8]}
      camera={{ position: [6, 2.6, 7], fov: 32, near: 0.05, far: 140 }}
      gl={{ antialias: true, alpha: true }}
      onCreated={({ gl }) => {
        gl.toneMapping = THREE.ACESFilmicToneMapping;
        gl.toneMappingExposure = 1;
      }}
      data-testid="hero-canvas"
    >
      <Environment />
      <ambientLight intensity={0.35} />
      <directionalLight position={[5, 7, 4]} intensity={2.2} color="#ffffff" />
      <directionalLight position={[-4, 3, 5]} intensity={0.7} color="#cfe0ff" />
      <spotLight position={[-5, 3, -4]} angle={0.95} penumbra={1} intensity={55} color="#ff1e42" distance={26} />
      <Floor />
      <Suspense fallback={null}>
        <Car anchorsRef={anchorsRef} shotRef={shotRef} />
      </Suspense>
      <Rig anchorsRef={anchorsRef} shotRef={shotRef} clockRef={clockRef} />
      <Pointer anchorsRef={anchorsRef} shotRef={shotRef} pointerRef={pointerRef} clockRef={clockRef} />
      <CabinLight anchorsRef={anchorsRef} shotRef={shotRef} clockRef={clockRef} />
      {dbg && <DebugAnchors anchorsRef={anchorsRef} />}
    </Canvas>
  );
};
